import { EmbeddedViewRef, Renderer2, TemplateRef, ViewContainerRef } from '@angular/core';
import { AuthSession } from '@shared/interfaces/auth-session.interface';
import { LoggerService } from '../services/logger.service';
import { AuthOptions } from '../models/auth-options.model';

/**
 * Abstract directive that manages authorization and conditional display of DOM elements
 *
 * Usage:
 * <div [data-auth]="{ id: 'PERMISSION_NAME || AUTHORITY_NAME', klass: 'custom-class' }">Protected content</div>
 */
export abstract class AbstractAuthDirective {
  protected rendered: boolean = false;
  protected hasOptions: boolean = false;
  protected viewRef: EmbeddedViewRef<any> | null = null;

  protected constructor(
    protected readonly loggerService: LoggerService,
    protected readonly viewContainerRef: ViewContainerRef,
    protected readonly templateRef: TemplateRef<unknown>,
    protected readonly renderer2: Renderer2,
    protected readonly authService: AuthSession
  ) {}

  /**
   * Processes the authorization logic
   * Can be overridden by child classes to change authorization flow
   * @param options Authorization options
   * @param type Scope of the authorization check, e.g., 'roles' or 'permissions'
   */
  protected abstract processAuthorization(options: AuthOptions, type?: string): void;

  /**
   * Checks if user has the required authority
   * Can be overridden by child classes
   * @param options Authorization options
   * @returns True if user has authority
   */
  protected checkAuthority(options: AuthOptions): boolean {
    if (!options.id) return false;
    return this.authService.hasAuthorities(options.id!, options.operator);
  }

  /**
   * Checks if user has the required permission
   * Can be overridden by child classes
   * @param options Authorization options
   * @returns True if user has permission
   */
  protected checkPermission(options: AuthOptions): boolean {
    if (!options.id) return false;
    if (options.exact) {
      return this.authService.hasExactPermissions(options.id, options.operator);
    }
    return this.authService.hasPermissions(options.id, options.operator);
  }

  /**
   * Validates the provided options
   * Can be overridden by child classes to add additional validation
   * @param options Options to validate
   * @returns true if options are valid, false otherwise
   */
  protected validateOptions(options: AuthOptions): boolean {
    if (!options.id) {
      this.loggerService.warn(this.constructor.name, "Empty 'id' attribute.");
      return false;
    }
    return true;
  }

  /**
   * Applies the options to the DOM element
   * Can be overridden by child classes to apply additional options
   * @param options Options to apply
   * @returns true if options were applied correctly
   */
  protected applyOptions(options: AuthOptions): boolean {
    this.hasOptions = false;
    // Create a view but don't insert it yet
    this.viewRef = this.templateRef.createEmbeddedView({});
    const renderElement = this.viewRef.rootNodes[0];
    if (options.klass) {
      this.setCSSClass(options, renderElement);
    }
    if (options.disabled) {
      this.setHTMLAttribute(options, renderElement, "disabled");
    }
    if (options.readonly) {
      this.setHTMLAttribute(options, renderElement, "readonly");
    }
    if (options.require) {
      this.setHTMLAttribute(options, renderElement, "require");
    }
    if (options.style) {
      this.setStyle(options, renderElement);
    }
    if (options.callback && (typeof options.callback === "function")) {
      this.executeCallback(options, renderElement);
    }
    // If there are no specific options, it's still valid
    return this.hasOptions || true;
  }

  /**
   * Executes the callback function
   * Can be overridden by child classes
   * @param options Authorization options
   * @param renderElement DOM element to apply callback to
   */
  protected executeCallback(options: AuthOptions, renderElement: HTMLElement): void {
    if (options.callback) {
      options.callback(renderElement);
    }
  }

  /**
   * Sets CSS classes to the element
   * Can be overridden by child classes
   */
  protected setCSSClass(options: AuthOptions, renderElement: HTMLElement): void {
    this.hasOptions = true;
    if (typeof options.klass === "string") {
      this.renderer2.addClass(renderElement, options.klass);
    } else if (Array.isArray(options.klass)) {
      options.klass.forEach((klass: string) =>
        this.renderer2.addClass(renderElement, klass)
      );
    }
  }

  /**
   * Sets CSS styles to the element
   * Can be overridden by child classes
   */
  protected setStyle(options: AuthOptions, renderElement: HTMLElement): void {
    this.hasOptions = true;
    const keys: Array<string> = Object.keys(options.style || {});
    keys.forEach(key => {
      this.renderer2.setStyle(renderElement, key, options.style[key]);
    });
  }

  /**
   * Sets an HTML attribute to the element
   * Can be overridden by child classes
   */
  protected setHTMLAttribute(options: AuthOptions, renderElement: HTMLElement, attribute: string): void {
    this.hasOptions = true;
    this.renderer2.setAttribute(renderElement, attribute, "true");
  }

  /**
   * Inserts the view into the container
   * Can be overridden by child classes
   */
  protected renderContent(): void {
    if (!this.rendered && this.viewRef) {
      this.viewContainerRef.insert(this.viewRef);
      this.rendered = true;
    }
  }

  /**
   * Clears the current view
   * Can be overridden by child classes
   */
  protected clearView(): void {
    this.viewContainerRef.clear();
    this.rendered = false;
    this.viewRef = null;
  }
}
